class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // body overflow alone doesn't stop the page scrolling on iOS / when html is the scroller
    document.documentElement.classList.add('nb-cart-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('nb-cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// The cart holds a single item: the line just added, at quantity 1. Other lines are removed,
// and adding the same item again (which Shopify merges into its line) doesn't raise the quantity.
// `added` is the /cart/add.js response: one line item, or { items: [...] }.
window.nibrewKeepOnlyAdded = async function nibrewKeepOnlyAdded(added) {
  const addedItem = added && (added.items ? added.items[0] : added);
  const cart = await (await fetch(`${routes.cart_url}.js`)).json();
  const keepKey = addedItem?.key || cart.items[0]?.key;
  if (!keepKey) return;

  const updates = {};
  cart.items.forEach((item) => {
    const quantity = item.key === keepKey ? 1 : 0;
    if (item.quantity !== quantity) updates[item.key] = quantity;
  });
  if (!Object.keys(updates).length) return;

  await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
};

// Called by the custom Nibrew add-to-cart forms after /cart/add.js succeeds, with its response.
// Opens the refreshed cart drawer, or goes to checkout when Theme settings > Cart > After add to cart says so.
window.nibrewAfterAddToCart = async function (added) {
  try {
    await window.nibrewKeepOnlyAdded(added);
  } catch (error) {
    console.error(error);
  }

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.dataset.afterAdd === 'checkout') {
    window.location.href = '/checkout';
    return;
  }

  try {
    const sectionIds = drawer.getSectionsToRender().map((section) => section.id);
    const response = await fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`);
    const sections = await response.json();
    drawer.classList.remove('is-empty');
    drawer.renderContents({ sections });
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
  } catch (error) {
    console.error(error);
    window.location.href = routes.cart_url;
  }
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Replaces a cart line with another variant (variant dropdown, upgrade button, subscribe link).
// Shopify can't change a line's variant in place, so the new variant is added and the old line removed.
class CartDrawerSwap extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('[data-dropdown-toggle]');
    this.list = this.querySelector('[role="listbox"]');
    this.onOutsideClick = (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    };

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-dropdown-toggle]')) {
        this.setOpen(this.list.hidden);
        return;
      }

      const button = event.target.closest('[data-target-variant]');
      if (!button) return;
      event.preventDefault();
      this.setOpen(false);
      this.swap(button.dataset.targetVariant, button.dataset.targetPlan);
    });

    this.addEventListener('keydown', (event) => {
      if (!this.list || this.list.hidden) return;
      const options = [...this.list.querySelectorAll('[data-target-variant]')];
      const index = options.indexOf(document.activeElement);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        options[(index + step + options.length) % options.length]?.focus();
      }
    });

    // Escape closes the dropdown without closing the whole drawer (which listens for keyup)
    this.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape' || !this.list || this.list.hidden) return;
      event.stopPropagation();
      this.setOpen(false);
      this.toggle.focus();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
  }

  setOpen(open) {
    if (!this.list) return;
    this.list.hidden = !open;
    this.toggle.setAttribute('aria-expanded', open);
    this.classList.toggle('is-open', open);

    if (open) {
      document.addEventListener('click', this.onOutsideClick);
      this.list.querySelector('[data-target-variant]')?.focus();
    } else {
      document.removeEventListener('click', this.onOutsideClick);
    }
  }

  async swap(variantId, sellingPlan = '') {
    const { lineKey, quantity, variantId: currentVariant, sellingPlan: currentPlan = '' } = this.dataset;
    if (String(variantId) === currentVariant && sellingPlan === currentPlan) return;

    const drawerInner = this.closest('.drawer__inner');
    const errors = document.getElementById('CartDrawer-CartErrors');
    drawerInner.classList.add('is-updating');
    if (errors) errors.textContent = '';

    const item = { id: Number(variantId), quantity: Number(quantity) };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    const properties = JSON.parse(this.dataset.properties || '{}');
    if (properties && Object.keys(properties).length) item.properties = properties;

    try {
      const addResponse = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items: [item] }),
      });
      const added = await addResponse.json();
      if (!addResponse.ok || added.status) throw new Error(added.description || added.message);

      const changeResponse = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const state = await changeResponse.json();
      if (!changeResponse.ok || state.status) throw new Error(state.description || state.message);

      const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      drawerInner.innerHTML = parse(state.sections['cart-drawer'], '.drawer__inner').innerHTML;
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = parse(state.sections['cart-icon-bubble'], '.shopify-section').innerHTML;

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId });
    } catch (error) {
      console.error(error);
      // Re-sync the drawer in case the new variant was added but the old line wasn't removed
      document.querySelector('cart-drawer-items')?.onCartUpdate();
      const errorsAfter = document.getElementById('CartDrawer-CartErrors');
      if (errorsAfter) errorsAfter.textContent = error.message || window.cartStrings.error;
    } finally {
      drawerInner.classList.remove('is-updating');
    }
  }
}

customElements.define('cart-drawer-swap', CartDrawerSwap);
